import { useState } from 'react';
import { Card } from '../../../../layout/Card.jsx';
import { Icon } from '../../../../layout/Icon.jsx';
import { Pill } from '../../../../layout/Pill.jsx';
import { useAudit } from '../../hooks/useAudit.js';
import { AuditFilters } from './AuditFilters.jsx';
import { AuditDetailModal } from './AuditDetailModal.jsx';
import { ViewAllToggle, ViewAllNotice } from '../../../../shared/ViewAllToggle.jsx';
import { VIEW_ALL_LIMIT } from '../../../../shared/viewAll.js';
import {
  labelForAction,
  labelForModule,
  labelForSeverity,
  variantForSeverity,
  hasActionLabel,
  formatAuditDate,
  formatAuditTime
} from './auditLabels.js';

function asArray(d) {
  return Array.isArray(d) ? d : d?.entries || d?.logs || d?.data || d?.items || [];
}

export function AuditLogTable() {
  const [filters, setFilters] = useState({ page: 1 });
  const [active, setActive] = useState(null);
  const [showAll, setShowAll] = useState(false);

  // «عرض الكل»: صفحة واحدة بسقف كبير، وتغيير أي فلتر لا يعيدنا للصفحات.
  const query = showAll ? { ...filters, page: 1, limit: VIEW_ALL_LIMIT } : filters;
  const { data, isLoading, error } = useAudit(query);
  const rows = asArray(data);
  // المُعترض يفكّ {items,total,page,pages} إلى مصفوفة وينقل الحقول عليها،
  // فلا وجود لـ data.meta — وبدون هذا كان الترقيم يظنّ أن هناك صفحة واحدة دائماً.
  const meta = data?.meta || data?.pagination || {
    total: data?.total ?? rows.length,
    pages: data?.pages ?? 1,
    totalPages: data?.pages ?? 1
  };
  const totalPages = meta.totalPages || meta.pages || 1;

  return (
    <div className="space-y-3" dir="rtl">
      <div className="flex items-center justify-end">
        <ViewAllToggle
          all={showAll}
          onChange={(v) => { setShowAll(v); setFilters((f) => ({ ...f, page: 1 })); }}
          shown={rows.length}
          total={meta.total}
        />
      </div>

      <AuditFilters value={filters} onChange={setFilters} />

      {error ? (
        <Card>
          <div className="text-red text-center py-4">تعذّر تحميل سجل التدقيق</div>
        </Card>
      ) : isLoading ? (
        <Card>
          <div className="text-ink-3 text-center py-8">جاري التحميل...</div>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <div className="text-ink-3 text-center py-10 text-sm">لا توجد سجلات مطابقة</div>
        </Card>
      ) : (
        <Card>
          <table className="w-full text-[13px]" dir="rtl">
            <thead className="text-ink-2 text-[12px]">
              <tr className="border-b border-line">
                <th className="p-2.5 text-start">التاريخ والوقت</th>
                <th className="p-2.5 text-start">المستخدم</th>
                <th className="p-2.5 text-start">الإجراء</th>
                <th className="p-2.5 text-start">الوحدة</th>
                <th className="p-2.5 text-start">الخطورة</th>
                <th className="p-2.5 text-start">رقم السجل</th>
                <th className="p-2.5 text-start">عنوان IP</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const id = r._id || r.id;
                const sev = r.severity || 'info';
                const at = r.createdAt || r.timestamp;
                // المفتاح الخام يبقى في tooltip للدعم الفني، والعرض عربي دائماً.
                const actionLabel = labelForAction(r.action, r.module);
                return (
                  <tr
                    key={id}
                    className="border-b border-line cursor-pointer hover:bg-paper-2"
                    onClick={() => setActive(r)}
                  >
                    <td className="p-2.5 text-ink-3 whitespace-nowrap">
                      <div className="text-[12px] text-ink-2 font-semibold">{formatAuditDate(at)}</div>
                      <div className="text-[11.5px]">{formatAuditTime(at)}</div>
                    </td>
                    <td className="p-2.5 font-bold">
                      {r.userName || r.user?.fullName || r.user?.username
                        || r.userId?.fullName || r.userId?.username || 'النظام'}
                    </td>
                    <td className="p-2.5" title={r.action || ''}>
                      <span className="font-semibold">{actionLabel}</span>
                      {r.action && !hasActionLabel(r.action) ? (
                        <span className="text-ink-3 text-[11px] ms-1.5">({r.action})</span>
                      ) : null}
                    </td>
                    <td className="p-2.5 text-ink-2" title={r.module || ''}>
                      {labelForModule(r.module)}
                    </td>
                    <td className="p-2.5">
                      <Pill variant={variantForSeverity(sev)}>{labelForSeverity(sev)}</Pill>
                    </td>
                    <td className="p-2.5 font-mono text-[11.5px]" dir="ltr">{r.recordId || '—'}</td>
                    <td className="p-2.5 font-mono text-[11.5px] text-ink-3" dir="ltr">
                      {r.ipAddress || r.ip || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {showAll ? <ViewAllNotice all={showAll} shown={rows.length} total={meta.total} /> : null}

      <div className="flex items-center justify-between" style={{ display: showAll ? 'none' : undefined }}>
        <div className="text-[12px] text-ink-3">
          الصفحة {filters.page || 1} من {totalPages}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="btn"
            disabled={(filters.page || 1) <= 1}
            onClick={() => setFilters((f) => ({ ...f, page: Math.max(1, (f.page || 1) - 1) }))}
          >
            <Icon name="chevronR" size={14} /> السابق
          </button>
          <button
            type="button"
            className="btn"
            disabled={(filters.page || 1) >= totalPages}
            onClick={() => setFilters((f) => ({ ...f, page: (f.page || 1) + 1 }))}
          >
            التالي <Icon name="chevronL" size={14} />
          </button>
        </div>
      </div>

      <AuditDetailModal
        open={!!active}
        entryId={active?._id || active?.id}
        fallback={active}
        onClose={() => setActive(null)}
      />
    </div>
  );
}
